// import React from 'react';
// import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import SdgScoreClob from './_sdgScoreClob';
import Link from '@mui/material/Link';
import { NaceDetailArray, StringAffinityListKeyValuePair, AffinityDto } from 'models/dtoTypes';

const PREFIX = 'DoNoSignificantHarmNaceDetailBox';

const classes = {
  code: `${PREFIX}-code`,
  description: `${PREFIX}-description`,
  itemsContainer: `${PREFIX}-itemsContainer`,
  item: `${PREFIX}-item`,
};

const Root = styled('div')(() => ({
  [`& .${classes.code}`]: {
    width: 60,
    fontWeight: 'bold',
  },
  [`& .${classes.description}`]: {
    flex: 1,
  },
  [`& .${classes.itemsContainer}`]: {
    display: 'table',
    width: '100%',
    tableLayout: 'fixed',
  },
  [`& .${classes.item}`]: {},
}));

interface IProps {
  naceDetailArray: NaceDetailArray;
}

export default function DoNoSignificantHarmNaceDetailBox(props: IProps): JSX.Element {
  const { naceDetailArray } = props;
  const {
    naceActivityTitle,
    affinityDescriptor,
    overallAffinity,
    affinities,
    affinityDescriptorLink,
  } = naceDetailArray;

  const euTaxonomyLink = (link: string): JSX.Element => {
    return (
      <Link href={link} target="_blank">
        EU Taxonomy Compass website
      </Link>
    );
  };

  const renderTableHeadCell = (key: string, index?: number) => {
    return (
      <Box
        key={index}
        className={classes.item}
        sx={{
          p: 0,
          verticalAlign: 'middle',
          bgcolor: 'background.paper',
          display: 'table-cell',
        }}
      >
        {key}
      </Box>
    );
  };

  const renderTableCells = (affinityList?: AffinityDto[] | null) => {
    const defaultAffinityList: AffinityDto[] = [];
    return (affinityList || defaultAffinityList).map((item, index) => {
      return (
        <Box
          key={index}
          sx={{
            p: 0,
            verticalAlign: 'middle',
            bgcolor: 'background.paper',
            display: 'table-cell',
          }}
        >
          <div style={{ width: 'fit-content' }}>
            <SdgScoreClob withBorder={true} affinity={item} />
          </div>
        </Box>
      );
    });
  };

  const renderTableRow = (item: StringAffinityListKeyValuePair, index: number) => {
    const { key, value } = item;
    return (
      <Box
        key={index}
        className={classes.item}
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          display: 'table-row',
        }}
      >
        {renderTableHeadCell(key || '')}
        {renderTableCells(value)}
      </Box>
    );
  };

  const renderTable = (items: StringAffinityListKeyValuePair[]) => {
    // 'Circular Economy', 'Biodiversity', 'Water', 'Pollution Prevention'
    const hideKeys = [] as string[];
    const keys = items.map((i) => i.key);
    const visibleItemRows = items.filter((p) => p.key && !hideKeys.includes(p.key.toLowerCase()));

    return (
      <Box
        sx={{
          p: 2,
          width: '100%',
          bgcolor: 'background.paper',
        }}
      >
        {affinityDescriptor && <p style={{ marginTop: 0 }}>{affinityDescriptor}</p>}
        <div className={classes.itemsContainer}>
          <Box
            className={classes.item}
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              display: 'table-row',
            }}
          >
            {renderTableHeadCell('')}
            {keys.map((k, i) => renderTableHeadCell(k || '', i))}
          </Box>
          {visibleItemRows.map(renderTableRow)}
        </div>
        {affinityDescriptorLink && (
          <div style={{ marginTop: 0 }}>
            Regulatory mitigation details are available on the{' '}
            {euTaxonomyLink(affinityDescriptorLink)}
          </div>
        )}
      </Box>
    );
  };

  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        p: 0,
        mt: 1,
        mb: 1,
        border: '2px solid #808080',
      }}
    >
      <Root>
        <SdgScoreClob withBg={true} title={naceActivityTitle || ''} affinity={overallAffinity} />
        <Divider sx={{ borderBottom: '2px solid #808080' }} />
        {renderTable(affinities || [])}
      </Root>
    </Box>
  );
}
